'use client';

import Image from 'next/image';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Icon } from '@/components/ui/Icon';
import type { Project } from '@/data/resolve';
import { cn } from '@/lib/cn';

/**
 * Hero deck — the shipped work, running on a real machine.
 *
 * Design: a stock render says the company is in software; the company's own
 * screens, on hardware, say it ships. So the hero visual is a machine, and it
 * is built as actual geometry rather than a picture of one: the lid, the deck
 * and the front lip are separate planes standing in one `preserve-3d` scene,
 * the deck lies in the floor plane at 90° to the screen, and the lid hinges
 * off the deck's back edge. That is what makes the object hold together when
 * it turns — a flat mock-up shears, a real one parallaxes.
 *
 * Proportions are a 14" notebook's, in units of its own width: lid 1 : 0.66,
 * deck depth 0.70, thickness 0.05. Nothing here is eyeballed.
 *
 * The camera, not the object, does the moving. One pair of angles drives the
 * whole scene, fed by three things at once: a base pose, the pointer (damped,
 * so the thing has mass), and the page's own scrolling, which walks the view
 * around the machine as the section passes. The object itself only breathes —
 * a four-pixel float — the way a product render is never quite dead still.
 *
 * On the screen, each project is drawn on by a light edge sweeping the glass,
 * the way a plotter lays down a sheet. Never a dissolve: a dissolve is what a
 * slideshow does, a wipe is what a machine does. The pass runs whichever way
 * the deck is travelling, so going back is visibly going back. Nothing else is
 * on the panel — no captions, no links, no chrome. The work carries itself.
 *
 * It also takes instructions. A horizontal trackpad flick over the machine and
 * the ← / → keys both drive it, and the drawn keyboard answers: the two arrow
 * keys are the only lit keys on the deck, and the one you used flashes. That
 * is the whole affordance — no dots, no chevrons floating over the glass. The
 * control is on the machine, where a control belongs.
 *
 * A driven step is not the idle one played on command. It runs a third faster,
 * its light edge is thicker, the room takes more of the flash, and the machine
 * itself takes the push and settles. Nothing new appears on the screen: the
 * difference is entirely in how fast and how hard, which is the difference
 * between a thing running and a thing answering.
 *
 * Screenshots arrive in every aspect ratio (2:1 web grabs, 4:3 phone stacks,
 * one full portrait), so the sharp image is always `contain`ed and the void
 * behind it is filled with a heavily blurred, dimmed copy of the same file —
 * the panel's own backlight. Nothing is ever cropped, and the fill is fetched
 * at 64px wide, so it costs kilobytes rather than a second image.
 *
 * Everything animated here is `transform`, `opacity`, `clip-path` or `filter`.
 * The loop stops when the deck leaves the viewport or the tab is hidden, the
 * pointer loop parks itself once it has caught up, and `prefers-reduced-motion`
 * holds the first project on screen with no cycling at all — the rotation *is*
 * the motion, and every project is listed in full further down the page.
 */

/** How long one project holds the screen, its own wipe-in included. */
const CYCLE = 4600;
/** Dolly, lid and wake run before the deck starts counting. Mirrors the
 *  animation delays in `globals.css` — change the two together. */
const INTRO = 1250;
/** A screenshot mounts this long after the previous one lands: late enough to
 *  stay out of the LCP window, early enough to be decoded before its turn. */
const PREFETCH = 1300;
/** Waiting on a screenshot that has not finished loading, ms between checks. */
const RETRY = 200;

/** Horizontal wheel travel, in CSS pixels, that commits a trackpad swipe. A
 *  two- or three-finger flick clears this in a few frames; a thumb nudging a
 *  mouse wheel sideways never does. */
const WHEEL = 84;
/** Touch drag that commits a swipe, as a fraction of the machine's width. */
const REACH_X = 0.16;
/** A gesture is over once no horizontal wheel event has arrived for this long
 *  — a trackpad ends its flick with a decaying tail, not with an event. */
const SETTLE = 150;
/** How long an arrow key on the drawn keyboard stays lit after it is used. */
const LIT = 260;
/** A driven step is held under this flag for the length of its own, shorter
 *  pass — see `data-drive` in the stylesheet. */
const DRIVEN = 760;
/** Wheel deltas arrive in pixels, lines or pages; normalise to pixels. */
const DELTA_UNIT = [1, 16, 100] as const;

/*
 * The keyboard, row by row, in key units — a 14" notebook's. Built as keys
 * rather than as a grating because a grating cannot give the two things that
 * make a keyboard read as one: the modifier stagger down the left edge, and
 * the inverted-T the ← / → keys sit in. It is ~70 static spans that never
 * animate, paint once, and cost nothing per frame.
 *
 * Every row sums to 15. The bottom row is written out in the markup instead,
 * because the arrow cluster is the one place a real machine breaks the grid.
 */
const KEY_ROWS: readonly (readonly number[])[] = [
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.25],
  [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.75],
];

/** Bottom row up to the arrow cluster: fn, control, option, command, space. */
const BOTTOM_ROW = [1, 1, 1, 1.25, 4.7, 1.25, 1] as const;

/*
 * The arrow cluster, in the same units. Wider than a stock notebook's — the
 * space bar gives up the difference — because on this machine ← and → are not
 * two keys among seventy-eight, they are the control the visitor is offered.
 * Left, the ↑/↓ column and right still come to 3.8, so the row closes at 15.
 */
const NAV_KEY = 1.4;
const UPDOWN_KEY = 1;

/** Fields that own the arrow keys while they are being used. */
const TYPING = /^(?:INPUT|TEXTAREA|SELECT)$/;

/** Resting camera. Most of the downward view comes from the eye being high
 *  (see `perspective-origin` in the stylesheet); this only tops it up, so the
 *  screen is never foreshortened for the sake of showing the deck. */
const BASE_X = -3;
const BASE_Y = -4;
/** Pointer authority over the camera, in degrees at the far edge. */
const PTR_X = 3.4;
const PTR_Y = 5;
/** Scroll authority: the view walks around the machine as the section goes by. */
const SCROLL_X = 4.5;
const SCROLL_Y = 5.5;
/** Distance, in rect widths from centre, at which the pointer reaches full. */
const REACH = 0.9;
/** Camera spring, e-folds per second. */
const FOLLOW = 5.5;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/* The motion preference is read as an external store rather than copied into
   state, so a visitor who turns reduced motion on mid-visit is obeyed at once
   and the server render has a defined answer. */
const REDUCED = '(prefers-reduced-motion: reduce)';

const subscribeReduced = (onChange: () => void) => {
  const query = window.matchMedia(REDUCED);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
};
const readReduced = () => window.matchMedia(REDUCED).matches;
const readReducedOnServer = () => false;

type HeroDeckProps = {
  /** The reel, already joined to the current language's copy. */
  projects: readonly Project[];
  /** Accessible names — the panel itself carries no visible text. */
  labels: { deck: string; previous: string; next: string };
  className?: string;
};

export function HeroDeck({ projects: deck, labels, className }: HeroDeckProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);

  /** `prev` stays lit under the incoming screen while the wipe crosses it;
   *  `dir` is which way the deck last travelled, which the wipe follows. */
  const [slide, setSlide] = useState({ index: 0, prev: -1, dir: 1 });
  /** Indices that have been put in the DOM so far — the deck grows forward. */
  const [mounted, setMounted] = useState<readonly number[]>([0]);
  /** True until the opening sequence has played out. */
  const [intro, setIntro] = useState(true);
  /** On screen and in a visible tab. */
  const [live, setLive] = useState(false);
  /** Pointer resting on the machine. */
  const [parked, setParked] = useState(false);

  /** Under reduced motion the deck holds its first project: the rotation is
   *  the motion, and every project is listed in full further down the page. */
  const still = useSyncExternalStore(
    subscribeReduced,
    readReduced,
    readReducedOnServer,
  );

  const loaded = useRef(new Set<number>([]));
  const first = useRef(true);
  /** Where the deck is, readable from a timer or an event without making
   *  either of them depend on a render. */
  const cursor = useRef(0);

  /** The two flags a driven step raises on the root, and their timers. */
  const flash = useRef(0);
  const driven = useRef(0);

  /** One step, whoever asked for it. */
  const advance = useCallback((dir: 1 | -1) => {
    const index = (cursor.current + dir + deck.length) % deck.length;
    if (index === cursor.current) return;
    cursor.current = index;
    setSlide((current) => ({ index, prev: current.index, dir }));
    setMounted((list) => (list.includes(index) ? list : [...list, index]));
  }, [deck]);

  /*
   * One *driven* step — a key struck, a key clicked, a flick landed. The two
   * flags go straight onto the node rather than through state, because they
   * have to be on the root before React mounts the new screen: a render is a
   * frame too late, and the pass would start without them.
   */
  const drive = useCallback(
    (dir: 1 | -1) => {
      const root = rootRef.current;
      if (!root) return;

      root.dataset.key = dir > 0 ? 'right' : 'left';
      root.dataset.drive = '';
      window.clearTimeout(flash.current);
      window.clearTimeout(driven.current);
      flash.current = window.setTimeout(() => delete root.dataset.key, LIT);
      driven.current = window.setTimeout(
        () => delete root.dataset.drive,
        DRIVEN,
      );

      /* A visitor steering the deck can be at any project in two moves, so the
         rest of the portfolio stops being lazy: an empty panel mid-pass is
         worse than a handful of screenshots fetched early. */
      setMounted((list) =>
        list.length === deck.length ? list : deck.map((_, index) => index),
      );
      advance(dir);
    },
    [advance, deck],
  );

  useEffect(
    () => () => {
      window.clearTimeout(flash.current);
      window.clearTimeout(driven.current);
    },
    [],
  );

  /* The opening sequence is a one-shot; the flag only exists to retime the
     first screen and is dropped once that is spent. */
  useEffect(() => {
    const id = window.setTimeout(() => setIntro(false), INTRO + 1500);
    return () => window.clearTimeout(id);
  }, []);

  /* Nothing runs off screen or in a background tab. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let onScreen = false;
    const sync = () =>
      setLive(onScreen && document.visibilityState === 'visible');

    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = Boolean(entry?.isIntersecting);
        sync();
      },
      { rootMargin: '120px 0px' },
    );
    io.observe(root);
    document.addEventListener('visibilitychange', sync);

    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  /* The cycle. A slide is never advanced onto a screenshot that has not
     finished loading — an empty frame would read as a broken panel — so the
     tick simply waits and asks again. */
  useEffect(() => {
    if (!live || parked || still || deck.length < 2) return;

    let id = 0;
    const tick = () => {
      const next = (cursor.current + 1) % deck.length;
      if (!loaded.current.has(next)) {
        id = window.setTimeout(tick, RETRY);
        return;
      }
      advance(1);
    };

    id = window.setTimeout(tick, first.current ? INTRO + CYCLE : CYCLE);
    first.current = false;

    return () => window.clearTimeout(id);
    /* `slide` is the restart signal: every step, driven or automatic, gives
       the next one a full cycle to itself. */
  }, [live, parked, still, slide, advance, deck]);

  /* One screenshot ahead, never the whole portfolio at once. */
  useEffect(() => {
    if (still || deck.length < 2) return;
    const next = (slide.index + 1) % deck.length;
    if (mounted.includes(next)) return;

    const id = window.setTimeout(
      () =>
        setMounted((list) => (list.includes(next) ? list : [...list, next])),
      PREFETCH,
    );
    return () => window.clearTimeout(id);
  }, [slide, mounted, still, deck]);

  /*
   * The camera.
   *
   * Pointer and scroll both push the same two angles, and a spring pulls them
   * there — a heavy object being turned, not a card snapping to the cursor.
   * The loop parks itself as soon as it has caught up and is woken by the next
   * input, so an untouched hero costs nothing per frame.
   */
  useEffect(() => {
    const root = rootRef.current;
    const scene = sceneRef.current;
    if (!root || !scene || !live || still) return;

    const finePointer = window.matchMedia(
      '(hover: hover) and (pointer: fine)',
    ).matches;

    let pointerX = 0;
    let pointerY = 0;
    let travel = 0;
    let targetX = BASE_X;
    let targetY = BASE_Y;
    let x = BASE_X;
    let y = BASE_Y;
    let last = 0;
    let raf = 0;

    const aim = () => {
      targetX = BASE_X + pointerY * PTR_X + travel * SCROLL_X;
      targetY = BASE_Y + pointerX * PTR_Y + travel * SCROLL_Y;
    };

    const write = () => {
      scene.style.setProperty('--cam-x', `${x.toFixed(2)}deg`);
      scene.style.setProperty('--cam-y', `${y.toFixed(2)}deg`);
      /* The glass reflection slides against the turn, which is the whole
         reason a screen reads as glass and not as paint. */
      scene.style.setProperty('--sheen-x', ((y - BASE_Y) / PTR_Y).toFixed(3));
      scene.style.setProperty('--sheen-y', ((x - BASE_X) / PTR_X).toFixed(3));
    };

    const frame = (now: number) => {
      /* Capped so a backgrounded tab cannot resume with one huge step. */
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
      last = now;

      const k = 1 - Math.exp(-FOLLOW * dt);
      x += (targetX - x) * k;
      y += (targetY - y) * k;
      write();

      if (Math.abs(targetX - x) < 0.002 && Math.abs(targetY - y) < 0.002) {
        x = targetX;
        y = targetY;
        write();
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(frame);
    };

    const wake = () => {
      if (raf) return;
      last = 0;
      raf = requestAnimationFrame(frame);
    };

    const onMove = (event: PointerEvent) => {
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      pointerX = clamp(
        (event.clientX - (rect.left + rect.width / 2)) / (rect.width * REACH),
        -1,
        1,
      );
      pointerY = clamp(
        (event.clientY - (rect.top + rect.height / 2)) / (rect.height * REACH),
        -1,
        1,
      );
      aim();
      wake();
    };

    /* Read in the frame, never in the scroll handler: one layout read per
       painted frame instead of one per event. */
    const onScroll = () => {
      const rect = root.getBoundingClientRect();
      const middle = rect.top + rect.height / 2;
      travel = clamp(
        (window.innerHeight / 2 - middle) / (window.innerHeight * 0.75),
        -1,
        1,
      );
      aim();
      wake();
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    if (finePointer) {
      window.addEventListener('pointermove', onMove, { passive: true });
    }
    wake();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointermove', onMove);
      if (raf) cancelAnimationFrame(raf);
      for (const prop of ['--cam-x', '--cam-y', '--sheen-x', '--sheen-y']) {
        scene.style.removeProperty(prop);
      }
    };
  }, [live, still]);

  /*
   * Direct control.
   *
   * Two ways in, one behaviour. A horizontal trackpad flick over the machine
   * and the ← / → keys both hand the deck a direction, and the gesture is
   * *followed* while it happens: the screens lean with the fingers and spring
   * back if the flick falls short. A swipe that only reports its verdict at
   * the end is a button wearing a gesture's clothes.
   *
   * Horizontal wheel events are swallowed, which is also what stops the
   * browser reading the same flick as "go back a page". Vertical ones are left
   * alone, so the page still scrolls through the hero untouched.
   */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !live || deck.length < 2) return;

    /** Signed progress of the gesture in hand, −1..1, forward positive. */
    const lean = (amount: number) =>
      root.style.setProperty('--drag', amount.toFixed(3));

    let travel = 0;
    let spent = false;
    let idle = 0;

    const rest = () => {
      travel = 0;
      spent = false;
      lean(0);
    };

    const onWheel = (event: WheelEvent) => {
      /* A page scroll passing over the machine is not a swipe. */
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();

      window.clearTimeout(idle);
      idle = window.setTimeout(rest, SETTLE);

      /* One flick, one project: the tail of a trackpad gesture must not run
         the whole portfolio past. */
      if (spent) return;

      travel += event.deltaX * (DELTA_UNIT[event.deltaMode] ?? 1);
      if (Math.abs(travel) < WHEEL) {
        lean(travel / WHEEL);
        return;
      }
      spent = true;
      lean(0);
      drive(travel > 0 ? 1 : -1);
    };

    /* Touch: the same gesture with a finger on the glass. */
    let touch = -1;
    let originX = 0;
    let originY = 0;
    let axis: 'x' | 'y' | null = null;

    const onDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' || touch !== -1) return;
      touch = event.pointerId;
      originX = event.clientX;
      originY = event.clientY;
      axis = null;
      travel = 0;
    };

    const onMove = (event: PointerEvent) => {
      if (event.pointerId !== touch) return;
      const dx = event.clientX - originX;
      const dy = event.clientY - originY;

      if (!axis) {
        /* Which way the drag went in its first few pixels decides who owns
           it, once. Re-deciding mid-drag is how a carousel steals a scroll. */
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') {
          touch = -1;
          return;
        }
        root.setPointerCapture(event.pointerId);
      }

      travel = dx;
      lean(clamp(-dx / (root.clientWidth * REACH_X), -1, 1));
    };

    const onUp = (event: PointerEvent) => {
      if (event.pointerId !== touch) return;
      touch = -1;
      const reached = -travel / (root.clientWidth * REACH_X);
      rest();
      if (Math.abs(reached) >= 1) drive(reached > 0 ? 1 : -1);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      /* Never take the arrows away from something being typed in. */
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        (active.isContentEditable || TYPING.test(active.tagName))
      ) {
        return;
      }
      drive(event.key === 'ArrowRight' ? 1 : -1);
    };

    /* Not passive: the whole point is to take the flick off the browser. */
    root.addEventListener('wheel', onWheel, { passive: false });
    root.addEventListener('pointerdown', onDown, { passive: true });
    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerup', onUp, { passive: true });
    root.addEventListener('pointercancel', onUp, { passive: true });
    window.addEventListener('keydown', onKey);

    return () => {
      root.removeEventListener('wheel', onWheel);
      root.removeEventListener('pointerdown', onDown);
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerup', onUp);
      root.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(idle);
      root.style.removeProperty('--drag');
    };
  }, [drive, live, deck]);

  return (
    <div
      ref={rootRef}
      data-intro={intro || undefined}
      data-parked={parked || undefined}
      data-dir={slide.dir < 0 ? 'back' : undefined}
      /* `data-drive` and `data-key` are written straight to the node by
         `drive`, not from here: they have to be on the root before the new
         screen mounts, and a render is a frame too late for that. */
      className={cn('deck', className)}
    >
      {/* Screen light on the room: a steady wash, plus one swell riding each
          wipe — the panel repainting itself. */}
      <span aria-hidden="true" className="deck__ambient" />
      {!still && (
        <span key={slide.index} aria-hidden="true" className="deck__flare" />
      )}

      {/* Dolly (the arrival move) and scene (the live camera) are separate
          elements on purpose: an animation that fills `both` owns `transform`
          forever, and would freeze the camera it is meant to hand over to. */}
      <div className="deck__dolly">
        <div ref={sceneRef} className="deck__scene">
          <div
            className="deck__lift"
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse') setParked(true);
            }}
            onPointerLeave={() => setParked(false)}
          >
            <div className="deck__float">
              <div className="deck__lid">
                {/* Seen only while the machine is opening: the back of the
                    lid, with the mark etched into it. */}
                <span aria-hidden="true" className="deck__lid-back">
                  <Icon name="layers" size={44} strokeWidth={1.5} />
                </span>

                <div className="deck__panel">
                  <div
                    role="img"
                    aria-label={labels.deck}
                    className="deck__screen"
                  >
                    {/* The screens ride on their own plane, so a gesture can
                        lean them without touching the wipe, the glass or the
                        light edge — those belong to the panel, not to the
                        work. */}
                    <div className="deck__reel">
                      {mounted.map((index) => {
                        const project = deck[index];
                        if (!project) return null;

                        const state =
                          index === slide.index
                            ? 'in'
                            : index === slide.prev
                              ? 'under'
                              : 'off';

                        return (
                          <div
                            key={project.slug}
                            className={cn(
                              'deck__layer',
                              `deck__layer--${state}`,
                            )}
                          >
                            {/* Backlight: the same file at 64px wide, blown up
                                and blurred, so no shot is ever cropped. */}
                            <div className="deck__fill">
                              <Image
                                src={project.image}
                                alt=""
                                fill
                                sizes="64px"
                                loading={index === 0 ? 'eager' : 'lazy'}
                                decoding="async"
                                className="deck__fill-img"
                              />
                            </div>

                            <div className="deck__shot">
                              <Image
                                src={project.image}
                                alt=""
                                fill
                                // The first screenshot is the hero visual, so
                                // it is fetched eagerly — but without a
                                // high-priority preload that would compete
                                // with the document, CSS and font in the LCP
                                // window.
                                loading={index === 0 ? 'eager' : 'lazy'}
                                decoding="async"
                                sizes="(min-width: 1024px) 44vw, (min-width: 640px) 88vw, 94vw"
                                onLoad={() => loaded.current.add(index)}
                                // A file that will not load must not stall the
                                // deck.
                                onError={() => loaded.current.add(index)}
                                className="deck__shot-img"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* One pass across the glass as the panel comes up — the
                        machine checking its own screen before it starts
                        showing work. The opening only: a project changing is
                        the screens moving, not a light being drawn on them. */}
                    {intro && !still && (
                      <span aria-hidden="true" className="deck__scan" />
                    )}

                    <span aria-hidden="true" className="deck__glass">
                      <span className="deck__glass-inner" />
                    </span>
                    <span aria-hidden="true" className="deck__vignette" />

                    {/* Dark until the lid has swung up. Dropped from the DOM
                        once the opening has played. */}
                    {intro && (
                      <span aria-hidden="true" className="deck__wake" />
                    )}
                  </div>

                  <span aria-hidden="true" className="deck__cam" />
                </div>
              </div>

              {/* The deck, lying in the floor plane at 90° to the screen. */}
              <div className="deck__body">
                <span aria-hidden="true" className="deck__shadow" />
                <span aria-hidden="true" className="deck__hinge" />
                <span aria-hidden="true" className="deck__spill" />

                {/*
                  The keyboard — see KEY_ROWS. Every key on it is decoration
                  except two: ← and → are real buttons, so the control the
                  machine appears to offer is the control it actually is. The
                  rest is hidden from assistive tech key by key rather than at
                  the well, because a focusable button inside an `aria-hidden`
                  subtree is a trap.
                */}
                <div className="deck__keys">
                  {KEY_ROWS.map((row, r) => (
                    <span
                      key={r}
                      aria-hidden="true"
                      className="deck__krow"
                      data-fn={r === 0 ? '' : undefined}
                    >
                      {row.map((unit, k) => (
                        <span
                          key={k}
                          className="deck__key"
                          style={{ flex: unit }}
                        />
                      ))}
                    </span>
                  ))}

                  {/* Modifiers, the space bar, and the inverted-T. */}
                  <span className="deck__krow">
                    {BOTTOM_ROW.map((unit, k) => (
                      <span
                        key={k}
                        aria-hidden="true"
                        className="deck__key"
                        style={{ flex: unit }}
                      />
                    ))}

                    <button
                      type="button"
                      aria-label={labels.previous}
                      className="deck__key deck__key--nav"
                      data-nav="left"
                      style={{ flex: NAV_KEY }}
                      onClick={() => drive(-1)}
                    />
                    <span
                      aria-hidden="true"
                      className="deck__updown"
                      style={{ flex: UPDOWN_KEY }}
                    >
                      <span className="deck__key deck__key--half" />
                      <span className="deck__key deck__key--half" />
                    </span>
                    <button
                      type="button"
                      aria-label={labels.next}
                      className="deck__key deck__key--nav"
                      data-nav="right"
                      style={{ flex: NAV_KEY }}
                      onClick={() => drive(1)}
                    />
                  </span>
                </div>

                <span aria-hidden="true" className="deck__pad" />
                {/* Stands back up out of the floor: −90° + 90° = facing us. */}
                <span aria-hidden="true" className="deck__front">
                  <span className="deck__lip" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
